import moment from "moment";
import {
  ErrorState,
  ISelectableOption,
  JsonSchemaItemProperty,
  JsonSchemaProperty,
  PrimitiveValueType,
  RowScopedErrorsState,
  RowDataReducerState,
  RowDataObject,
  RowData,
  RowDataValueType,
  RowDataReducerAction,
  JsonSchemaItems,
  NestedObjectErrorState,
} from "../../../types";
import { getTitleFromTargetPropertyKey } from "../helpers";
import { customEvaluations } from "../evaluations";
import { getIntl } from "../../../translate/i18n";
import { messages } from "../messages";
import { getHumanFriendlyPatternDescription } from "./patternDescriptionHelper";
import { ROW_DATA_KEYS } from "../constants";

export const formatErrorMessage = (message: string): ErrorState => ({
  error: true,
  message,
});

export const noError = (): ErrorState => ({ error: false, message: "" });

export const extractErrorAndMessage = (
  propertyKey: string,
  rowIndex: number | undefined,
  propertySubKey: string | undefined,
  rowDataReducerState: RowDataReducerState,
  isDialogField: boolean | undefined,
): ErrorState => {
  const errors = isDialogField
    ? rowDataReducerState.dialogRowDataErrors
    : rowDataReducerState.rowDataErrors;

  if (!errors) {
    return { error: false, message: "" };
  }

  if (rowIndex !== undefined && propertySubKey) {
    const errorState = errors[propertyKey] as RowScopedErrorsState;
    return {
      error: errorState?.[rowIndex]?.[propertySubKey]?.error || false,
      message: errorState?.[rowIndex]?.[propertySubKey]?.message || "",
    };
  } else if (propertySubKey && rowIndex === undefined) {
    const errorState = errors[propertyKey] as NestedObjectErrorState;
    return {
      error: errorState?.[propertySubKey]?.error || false,
      message: errorState?.[propertySubKey]?.message || "",
    };
  } else {
    const errorState = errors[propertyKey] as ErrorState;
    return {
      error: errorState?.error || false,
      message: errorState?.message || "",
    };
  }
};

export const getEmptyFieldsMessage = (
  dynamicDependentFieldsAndValues:
    | Record<string, PrimitiveValueType>
    | undefined,
  propertyKey: string,
  rowIndex: number | undefined,
  rowDataReducerState: RowDataReducerState,
): ISelectableOption | null => {
  if (!dynamicDependentFieldsAndValues) return null;
  const intl = getIntl();
  const fields = Object.keys(dynamicDependentFieldsAndValues);
  if (fields.length === 0) return null;

  const emptyKeys = fields.filter(
    (key) =>
      dynamicDependentFieldsAndValues[key] === "" ||
      dynamicDependentFieldsAndValues[key] === null,
  );

  if (emptyKeys.length > 0) {
    const combinedTitle = emptyKeys
      .map((key) => {
        const removed$FromKey = key.startsWith("$") ? key.substring(1) : key;
        return getTitleFromTargetPropertyKey({
          targetKey: removed$FromKey,
          sourceKey: propertyKey,
          rowIndex,
          rowDataReducerState,
        });
      })
      .join(", ");

    return {
      label:
        intl.formatMessage({ id: "label.select", defaultMessage: "Select" }) +
        ` ${combinedTitle}`,
      value: "",
      disabled: true,
    };
  }

  return null;
};

export const validateString = (
  property: JsonSchemaProperty,
  newFieldValue: string,
): ErrorState => {
  const { minLength, maxLength, pattern, items } = property;

  if (minLength && newFieldValue.length < minLength) {
    return formatErrorMessage(
      getIntl().formatMessage(messages.minLength, { minLength }),
    );
  }
  if (maxLength && newFieldValue.length > maxLength) {
    return formatErrorMessage(
      getIntl().formatMessage(messages.maxLength, { maxLength }),
    );
  }
  if (pattern && !matchesPattern(pattern, newFieldValue)) {
    return formatErrorMessage(formatPatternValidationMessage(pattern));
  }

  if (items?.pattern && !matchesPattern(items.pattern, newFieldValue)) {
    return formatErrorMessage(formatPatternValidationMessage(items.pattern));
  }
  return noError();
};

export const formatPatternValidationMessage = (pattern: string): string => {
  return `Must be ${getHumanFriendlyPatternDescription(pattern)}`;
};

export const matchesPattern = (pattern: string, value: string): boolean => {
  const candidates = [pattern];
  const normalizedPattern = pattern.replace(/\\\\/g, "\\");
  const compactPattern = normalizedPattern.replace(/\s+/g, "");

  // Schema `|null` means null value is allowed by the model, not the literal string "null".
  const allowsNullAlternative = /(^|[|(])null($|[)|])/.test(compactPattern);
  if (allowsNullAlternative && value === "null") {
    return false;
  }

  if (normalizedPattern !== pattern) {
    candidates.push(normalizedPattern);
  }

  return candidates.some((candidate) => {
    try {
      return new RegExp(candidate).test(value);
    } catch {
      return false;
    }
  });
};

export const validateNumber = (
  property: JsonSchemaProperty | JsonSchemaItemProperty,
  newFieldValue: number,
): ErrorState => {
  const { minimum, maximum, multipleOf } = property;

  if (minimum !== undefined && newFieldValue < minimum) {
    return formatErrorMessage(
      getIntl().formatMessage(messages.minimum, { minimum }),
    );
  }
  if (maximum !== undefined && newFieldValue > maximum) {
    return formatErrorMessage(
      getIntl().formatMessage(messages.maximum, { maximum }),
    );
  }

  if (multipleOf !== undefined) {
    const ratio = newFieldValue / multipleOf;
    const isMultipleOf = Math.abs(ratio - Math.round(ratio)) < 1e-10;
    if (!isMultipleOf) {
      return formatErrorMessage(
        getIntl().formatMessage(messages.multipleOf, { multipleOf }),
      );
    }
  }
  return noError();
};

export const validateDateField = (
  format: string,
  newFieldValue: string,
): ErrorState => {
  if (format) {
    const dateFormat = format === "date" ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm:ss";
    if (
      newFieldValue !== null &&
      !moment(newFieldValue, dateFormat, true).isValid()
    ) {
      return formatErrorMessage(
        getIntl().formatMessage(messages.invalidDate, { dateFormat }),
      );
    }
  }
  return noError();
};

export const validateUniqueField = (
  propertyKey: string,
  newFieldValue: string | number,
  rowDataReducerState: RowDataReducerState,
): ErrorState => {
  const { rowDataList } = rowDataReducerState;

  const rowValueSet = new Set(
    rowDataList.map((row) =>
      (row[propertyKey] as string | number)?.toString().toLowerCase(),
    ),
  );

  const isUnique = !rowValueSet.has(newFieldValue.toString().toLowerCase());
  const intl = getIntl();
  return isUnique
    ? noError()
    : formatErrorMessage(
        intl.formatMessage(
          {
            id: "error.duplicateValue",
            defaultMessage: "{field} already exists.",
          },
          {
            field: getTitleFromTargetPropertyKey({
              targetKey: propertyKey,
              sourceKey: undefined,
              rowIndex: undefined,
              rowDataReducerState,
            }),
          },
        ),
      );
};

export const validateUniqueCoRelationField = (
  propertyKey: string,
  propertySubKey: string,
  rowIndex: number,
  newFieldValue: RowDataValueType,
  selectedTabSectionKey: string | undefined,
  rowDataReducerState: RowDataReducerState,
): ErrorState => {
  const { rowData } = rowDataReducerState;
  const rows = getCurrentRows(propertyKey, selectedTabSectionKey, rowData);

  const values: RowDataValueType[] = [];
  rows.forEach((row, index) => {
    if (index !== rowIndex) values.push(row[propertySubKey]);
  });

  const normalizedNewFieldValue = normalizeForKey(newFieldValue);
  const isUnique = !values.some(
    (value) => normalizeForKey(value) === normalizedNewFieldValue,
  );
  const intl = getIntl();
  return isUnique
    ? noError()
    : formatErrorMessage(
        intl.formatMessage(
          {
            id: "error.duplicateValue",
            defaultMessage: "{field} already exists.",
          },
          {
            field: getTitleFromTargetPropertyKey({
              targetKey: propertySubKey,
              sourceKey: propertyKey,
              rowIndex,
              rowDataReducerState,
            }),
          },
        ),
      );
};

export const normalizeForKey = (value: unknown): string => {
  if (value == null) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value).toLowerCase();
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (Array.isArray(value)) {
    return value.map(normalizeForKey).join("|");
  }

  if (typeof value === "object") {
    return Object.values(value).map(normalizeForKey).join("|");
  }

  return "";
};

export const validateUniqueFieldsCombination = (
  propertyKey: string,
  uniqueCombination: string[],
  newFieldValue: string | number,
  rowDataReducerState: RowDataReducerState,
): ErrorState => {
  const { rowData, rowDataList } = rowDataReducerState;

  const existingRecordId = rowData?.[ROW_DATA_KEYS.ID];

  const currentRecordFromList = rowDataList.find(
    (record) =>
      String(record[ROW_DATA_KEYS.ID] ?? "") === String(existingRecordId ?? ""),
  );

  // // Find the existing record's original combination.
  // const currentRecord = currentRecordFromList ?? resetState?.rowData;

  const newCombination = uniqueCombination
    .map((key) =>
      normalizeForKey(key === propertyKey ? newFieldValue : rowData[key]),
    )
    .join("::");

  // Allow the existing record to retain its current combination.
  if (currentRecordFromList) {
    const currentCombination = uniqueCombination
      .map((key) => normalizeForKey(currentRecordFromList[key]))
      .join("::");

    if (currentCombination === newCombination) {
      return noError();
    }
  }

  // Exclude the current record when checking for duplicate combinations.
  const existingCombinations = new Set(
    rowDataList
      .filter(
        (record) =>
          !currentRecordFromList ||
          String(record[ROW_DATA_KEYS.ID] ?? "") !==
            String(existingRecordId ?? ""),
      )
      .map((record) =>
        uniqueCombination.map((key) => normalizeForKey(record[key])).join("::"),
      ),
  );

  return existingCombinations.has(newCombination)
    ? formatErrorMessage(
        getIntl().formatMessage(
          {
            id: "error.duplicateCombination",
            defaultMessage: "{combination} combination already exists.",
          },
          {
            combination: uniqueCombination
              .map((key) =>
                getTitleFromTargetPropertyKey({
                  targetKey: key,
                  sourceKey: undefined,
                  rowIndex: undefined,
                  rowDataReducerState,
                }),
              )
              .join(", "),
          },
        ),
      )
    : noError();
};

export const hasDuplicateRows = (
  propertyKey: string,
  selectedTabSectionKey: string | undefined,
  rowDataReducerState: RowDataReducerState,
): ErrorState => {
  const { rowData, schema } = rowDataReducerState;
  const nestedProperties =
    (schema.properties[propertyKey]?.items as JsonSchemaItems)?.properties ??
    {};

  const rows = getCurrentRows(propertyKey, selectedTabSectionKey, rowData);
  const keys = Object.keys(nestedProperties);
  const uniqueEntries = new Set<string>();

  const hasDuplicate = rows.some((row) => {
    const uniqueKey = keys.map((key) => row[key]).join("-");
    if (uniqueEntries.has(uniqueKey)) return true;
    uniqueEntries.add(uniqueKey);
    return false;
  });

  const intl = getIntl();
  return hasDuplicate
    ? formatErrorMessage(
        intl.formatMessage(
          {
            id: "error.duplicateRows",
            defaultMessage: "{field} have duplicate rows.",
          },
          {
            field: schema.properties[propertyKey].title,
          },
        ),
      )
    : noError();
};

const getCurrentRows = (
  propertyKey: string,
  selectedTabSectionKey: string | undefined,
  rowData: RowData,
): RowDataObject[] => {
  if (selectedTabSectionKey) {
    return (
      ((rowData[selectedTabSectionKey] as RowData)?.[
        propertyKey
      ] as RowDataObject[]) ?? []
    );
  }
  return (rowData[propertyKey] as RowDataObject[]) ?? [];
};

export const validateIfEvalMethodNeeded = ({
  evalMethod,
  propertyKey,
  propertySubKey,
  rowIndex,
  newFieldValue,
  selectedTabSectionKey,
  rowDataReducerState,
  dispatch,
}: {
  evalMethod: string | undefined;
  propertyKey: string;
  propertySubKey: string | undefined;
  rowIndex: number | undefined;
  newFieldValue: RowDataValueType;
  selectedTabSectionKey: string | undefined;
  rowDataReducerState: RowDataReducerState;
  dispatch?: React.Dispatch<RowDataReducerAction>;
}): ErrorState => {
  if (!evalMethod || !dispatch) return noError();
  return (
    customEvaluations({
      evalMethod,
      propertyKey,
      propertySubKey,
      rowIndex,
      inputValue: newFieldValue,
      searchResponse: undefined,
      selectedTabSectionKey,
      rowDataReducerState,
      dispatch,
    }) ?? noError()
  );
};

export const checkItemsLengthIfNeeded = (
  subProperty: JsonSchemaItemProperty,
  newFieldValue: RowDataValueType,
): ErrorState => {
  if (!subProperty.maxItems) return noError();
  if (
    Array.isArray(newFieldValue) &&
    newFieldValue.length > subProperty.maxItems
  ) {
    const intl = getIntl();
    return formatErrorMessage(
      intl.formatMessage(
        {
          id: "validation.maxItems",
          defaultMessage: "Maximum {maxItems} values allowed",
        },
        {
          maxItems: subProperty.maxItems,
        },
      ),
    );
  }
  return noError();
};
